/**
 * anlage-ballsets — das Ergebnis eines Laufs: was entstanden ist, oder was schiefging.
 *
 * Der Block hat zwei Ausgaenge, und beide sind gleich wichtig. Der Erfolgsfall nennt die
 * IDs, mit denen der Nutzer in Plenty weiterarbeitet. Der Fehlerfall nennt den Schritt, den
 * Plenty-Fehlertext und — falls der Artikel schon existiert — die Item-ID, die jetzt inaktiv
 * im Shop liegt und aufgeraeumt werden muss (LAUF-06). Ein Lauf, der nur „fehlgeschlagen"
 * sagt, hinterlaesst Arbeit, die niemand findet.
 *
 * DER BLOCK DARF AN FEHLENDEN FELDERN NICHT ZERBRECHEN. `result` des Job-Hooks ist ein
 * `Record<string, any>`; dass darin ein vollstaendiges `AnlageErgebnis` steht, ist eine
 * Behauptung. Bricht der Lauf ab, bevor das Protokoll steht, kommt gar keins — dann zeigt
 * die rote Box nur `fehlerText`. Jede Liste laeuft deshalb ueber `?? []`.
 *
 * Plenty-Fehlertexte werden UMGEBROCHEN, nicht abgeschnitten (`break-words`): `err.response
 * .data` ist bei Plenty die einzige brauchbare Fehlerquelle, und was hier fehlt, sucht
 * danach jemand in den Logs.
 *
 * Das Badge-Schema kommt aus `Historie.tsx` (`badgeFarbe`, `schrittFarbe`) — dieselben
 * Status duerfen nicht zweimal verschieden eingefaerbt sein.
 */
import { AlertTriangle, Check, CheckCircle2, X } from 'lucide-react';
import { badgeFarbe, schrittFarbe } from './Historie';
import NachkontrolleBlock from './NachkontrolleBlock';
import { zahl } from '../formate';
import type { AnlageErgebnis, AnlageSchritt, AnlageSetVariante } from '../typen';

interface Props {
  /** `null`, wenn der Lauf vor dem Protokoll scheiterte — dann traegt `fehlerText` alles. */
  ergebnis: AnlageErgebnis | null;
  /** Der Status des Jobs aus `useJobStatus`. Nur `completed` und `failed` zeigen etwas. */
  jobStatus: string | null;
  /** Fehlermeldung des Jobs bzw. der Seite. */
  fehlerText?: string | null;
  /**
   * Ab 06-07: der Knopf „Erneut prüfen" im Nachkontroll-Block. Wird durchgereicht statt hier
   * ausgewertet — dieser Block weiss nichts von Jobs, die Seite haelt den Job-Hook.
   */
  onErneutPruefen?: () => void;
  /** Laeuft gerade ein Job? Sperrt den Knopf und beschriftet ihn mit „Prüft …". */
  laeuft?: boolean;
}

const PANEL = 'border border-[#DAD9D9] rounded-lg bg-white p-4';

/**
 * Klartext je Schritt-Art. Unbekanntes bleibt, wie es kam — geraten wird nichts.
 *
 * DIESE LISTE BESCHRIFTET ZWEI GENERATIONEN VON SCHRITT-ARTEN, und das ist Absicht:
 *
 * 1. Die AKTUELLEN Arten: `kopie`, `texte`, `variantenLoeschung`, `setVariante`,
 *    `hauptvariante`, `paket`, `barcodeLoeschung`, `preisLoeschung`. Nur diese acht kann
 *    `baueSchreibplan` heute noch erzeugen — der Artikel entsteht als Plenty-Kopie, danach
 *    werden die mitkopierten Kindvarianten entfernt und die Set-Varianten neu angelegt
 *    (`variantenLoeschung` seit dem 22.09.2026, Befund 6; `preisLoeschung` seit dem
 *    23.09.2026).
 * 2. Die ENTFALLENEN Arten: `item`, `bild`, `bildVerfuegbarkeit`, `bildVerknuepfung`,
 *    `eigenschaft`, `eigenschaftWert`, `tag`, `crossSelling`, `versandprofil`, `merkmal`.
 *    Es gibt sie nur noch in ALTEN Protokoll-Laeufen: `versandprofil` und `merkmal` reisen
 *    seit Plan 05-01 im Body von `POST /items` mit, `bildVerknuepfung` entfiel am
 *    16.09.2026 mit der Nutzer-Entscheidung gegen Bild-Varianten-Zuordnungen, und den Rest
 *    bringt seit Phase 7 die Kopie mit, statt ihn Eintrag fuer Eintrag zu schreiben.
 *
 * Wer die zweite Gruppe „aufraeumt", macht die Historie unleserlich: `artText` faellt dann
 * auf den rohen Schluessel zurueck, und im Protokoll eines aelteren Laufs stuende „merkmal"
 * statt „Merkmal". Die Eintraege kosten nichts und sind der einzige Grund, warum alte
 * Laeufe vollstaendig lesbar bleiben.
 */
const ART_TEXT: Record<string, string> = {
  // Die aktuellen Arten (Phase 7):
  kopie: 'Artikel kopieren',
  texte: 'Texte',
  variantenLoeschung: 'Mitkopierte Variante entfernen',
  setVariante: 'Set-Variante',
  hauptvariante: 'Hauptvariante',
  paket: 'Paket (Bundle)',
  barcodeLoeschung: 'Barcode entfernen',
  preisLoeschung: 'Staffelpreis entfernen',
  // Nur noch fuer alte Protokoll-Laeufe (siehe oben) — nicht entfernen:
  item: 'Artikel + Hauptvariante',
  bild: 'Bild',
  bildVerfuegbarkeit: 'Bild-Verfügbarkeit',
  bildVerknuepfung: 'Bild-Verknüpfung',
  eigenschaft: 'Eigenschaft',
  eigenschaftWert: 'Eigenschafts-Wert',
  tag: 'Tag',
  crossSelling: 'Cross-Selling',
  versandprofil: 'Versandprofil',
  merkmal: 'Merkmal',
};

const artText = (art: string) => ART_TEXT[art] ?? art;

/** Dauer lesbar: unter einer Sekunde in ms, darueber in Sekunden mit einer Stelle. */
function dauer(ms: number | undefined): string {
  if (ms == null || !isFinite(ms)) return '—';
  if (ms < 1000) return `${zahl(Math.round(ms))} ms`;
  return `${(ms / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} s`;
}

/** Die Schrittliste — dieselbe in beiden Ausgaengen: was lief, was nicht, was blieb offen. */
function Schrittliste({ schritte }: { schritte: AnlageSchritt[] }) {
  if (schritte.length === 0) {
    return <p className="text-xs text-gray-500 italic">Keine Schritte protokolliert.</p>;
  }
  return (
    <div>
      {schritte.map((s) => (
        <div key={s.id} className="flex items-baseline gap-2 py-0.5 text-[11px]">
          <span className={`w-24 shrink-0 ${schrittFarbe(s.status)}`}>{s.status}</span>
          <span className="w-16 shrink-0 font-mono text-gray-500">{s.id}</span>
          <span className="w-40 shrink-0 text-gray-600">{artText(s.art)}</span>
          {s.plentyId != null && <span className="text-gray-500">→ {s.plentyId}</span>}
          <span className="text-gray-400">{dauer(s.dauerMs)}</span>
          {s.hinweis && <span className="text-gray-600 break-words">{s.hinweis}</span>}
          {s.fehler && <span className="text-red-800 break-words">{s.fehler}</span>}
        </div>
      ))}
    </div>
  );
}

/** Eine Schritt-Art mit ihren Schritten und der Zaehlung, die darueber steht. */
interface ArtGruppe {
  art: string;
  schritte: AnlageSchritt[];
  ok: number;
  fehler: number;
  uebersprungen: number;
}

/**
 * Buendelt die Schritte nach Art — in der Reihenfolge ihres ERSTEN Vorkommens, nicht
 * alphabetisch: der Plan laeuft von oben nach unten, und die Anzeige soll dieselbe
 * Reihenfolge haben wie der Lauf. Wer eine lange Schrittliste am Stueck liest (18622 hat
 * 27 Schritte, aeltere Laeufe ueber 60), findet die eine fehlgeschlagene Loeschung nicht;
 * nach Art gebuendelt steht sie unter ihrer Ueberschrift.
 */
function gruppiereNachArt(schritte: AnlageSchritt[]): ArtGruppe[] {
  const gruppen: ArtGruppe[] = [];
  const nachArt = new Map<string, ArtGruppe>();
  for (const s of schritte) {
    let gruppe = nachArt.get(s.art);
    if (!gruppe) {
      gruppe = { art: s.art, schritte: [], ok: 0, fehler: 0, uebersprungen: 0 };
      nachArt.set(s.art, gruppe);
      gruppen.push(gruppe);
    }
    gruppe.schritte.push(s);
    if (s.status === 'ok') gruppe.ok += 1;
    else if (s.status === 'fehler') gruppe.fehler += 1;
    else if (s.status === 'uebersprungen') gruppe.uebersprungen += 1;
  }
  return gruppen;
}

/** Die Schritte nach Art gebuendelt — die Liste selbst bleibt die bekannte `Schrittliste`. */
function GruppierteSchritte({ schritte }: { schritte: AnlageSchritt[] }) {
  const gruppen = gruppiereNachArt(schritte ?? []);
  if (gruppen.length === 0) return <Schrittliste schritte={[]} />;
  return (
    <div className="space-y-2">
      {gruppen.map((g) => (
        <div key={g.art}>
          <div className="flex items-baseline gap-2 text-[11px] border-b border-[#DAD9D9] pb-0.5">
            <span className="font-semibold text-[#1A1A1A]">{artText(g.art)}</span>
            <span className="text-gray-500">
              {zahl(g.ok)} ok · {zahl(g.fehler)} Fehler · {zahl(g.uebersprungen)} übersprungen
            </span>
          </div>
          <Schrittliste schritte={g.schritte} />
        </div>
      ))}
    </div>
  );
}

/**
 * Die Set-Varianten des Ergebnisses. Steht in einer eigenen Komponente, weil sie im
 * Erfolgsfall UND im `teilweise`-Fall gebraucht wird: wer nacharbeiten muss, braucht die
 * neuen Varianten-IDs erst recht.
 */
function SetVariantenTabelle({ setVarianten }: { setVarianten: AnlageSetVariante[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-[#1A1A1A] text-white">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Schritt</th>
            <th className="px-2 py-1.5 text-left font-medium">Original-Variante</th>
            <th className="px-2 py-1.5 text-right font-medium">Setgröße</th>
            <th className="px-2 py-1.5 text-right font-medium">neue Varianten-ID</th>
            <th className="px-2 py-1.5 text-center font-medium">Paket</th>
          </tr>
        </thead>
        <tbody>
          {setVarianten.length === 0 && (
            <tr>
              <td colSpan={5} className="px-2 py-1.5 text-gray-500 italic">
                Keine Set-Varianten im Ergebnis.
              </td>
            </tr>
          )}
          {setVarianten.map((v) => (
            <tr key={v.schrittId} className="border-b border-[#DAD9D9] last:border-0">
              <td className="px-2 py-1.5 font-mono text-gray-500">{v.schrittId}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">{v.originalNumber ?? '—'}</td>
              <td className="px-2 py-1.5 text-right">
                {v.setgroesse != null ? zahl(v.setgroesse) : '—'}
              </td>
              <td className="px-2 py-1.5 text-right">{v.variationId ?? '—'}</td>
              <td className="px-2 py-1.5 text-center">
                {v.paketOk
                  ? <Check size={13} className="inline text-green-800" />
                  : <X size={13} className="inline text-red-800" />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Set-Varianten, gruppierte Schritte und die Zahl der erledigten Schritte — beide Ausgaenge. */
function ErgebnisDetails({
  ergebnis,
  schritte,
  setVarianten,
  onErneutPruefen,
  laeuft,
}: {
  ergebnis: AnlageErgebnis;
  schritte: AnlageSchritt[];
  setVarianten: AnlageSetVariante[];
  onErneutPruefen?: () => void;
  laeuft?: boolean;
}) {
  return (
    <div className="space-y-3">
      <SetVariantenTabelle setVarianten={setVarianten} />
      <div>
        <h3 className="text-xs font-bold text-[#1A1A1A] mb-1">Schritte</h3>
        <GruppierteSchritte schritte={schritte} />
      </div>
      {/*
        Die Nachkontrolle steht hier und nicht in den beiden Ausgaengen einzeln: genau dafuer
        gibt es `ErgebnisDetails` (siehe Kommentar oben) — der Abschnitt erscheint dadurch im
        Erfolgsfall UND im `teilweise`-Fall, ohne dass jemand zwei Stellen pflegen muss.
        `?? null` ist Pflicht: ein Lauf, der vor der Nachkontrolle abbricht, liefert das Feld
        gar nicht, und der Block zeigt dann nichts statt einer leeren Karte.
      */}
      <NachkontrolleBlock
        nachkontrolle={ergebnis?.nachkontrolle ?? null}
        onErneutPruefen={onErneutPruefen}
        laeuft={laeuft}
      />
      <p className="text-xs text-gray-600">
        {zahl(ergebnis.writesAusgefuehrt ?? 0)} Schreibvorgänge ausgeführt.
      </p>
    </div>
  );
}

export default function ErgebnisBlock({ ergebnis, jobStatus, fehlerText, onErneutPruefen, laeuft }: Props) {
  // Vor dem Ende gibt es nichts zu zeigen — der Fortschritt steht woanders.
  if (jobStatus !== 'completed' && jobStatus !== 'failed') return null;

  const schritte: AnlageSchritt[] = ergebnis?.schritte ?? [];
  const setVarianten = ergebnis?.setVarianten ?? [];

  // ---------- Fehlerfall ----------
  if (jobStatus === 'failed' || ergebnis?.status === 'fehlgeschlagen') {
    const gescheitert = schritte.find((s) => s.status === 'fehler');
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 space-y-2">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <AlertTriangle size={16} /> Der Lauf ist fehlgeschlagen
        </h2>

        {gescheitert && (
          <p className="text-xs break-words">
            Fehlgeschlagener Schritt: <span className="font-mono">{gescheitert.id}</span>{' '}
            ({artText(gescheitert.art)})
            {gescheitert.fehler && <> — {gescheitert.fehler}</>}
          </p>
        )}

        {fehlerText && <p className="text-xs break-words">{fehlerText}</p>}

        {ergebnis?.ballsetItemId != null && (
          /* Gilt seit Phase 7 auch fuer eine KOPIE, die nach dem Kopier-Schritt haengen
             geblieben ist: der Artikel steht dann vollstaendig kopiert in Plenty — inklusive
             der Barcodes des Originals, die der Lauf nicht mehr entfernen konnte. */
          <p className="text-xs break-words bg-white/60 border border-red-200 rounded px-3 py-2">
            <strong>Artikel {ergebnis.ballsetItemId} wurde bereits angelegt.</strong> Er liegt
            inaktiv in Plenty und muss von Hand geprüft oder gelöscht werden — dieses Programm
            räumt nicht auf, und ein zweiter Versuch legt einen weiteren Artikel an.
            {ergebnis.hauptVariationId != null && (
              <> Hauptvariante: {ergebnis.hauptVariationId}.</>
            )}
          </p>
        )}

        {!ergebnis && !fehlerText && (
          <p className="text-xs">Kein Ergebnis übermittelt — Einzelheiten stehen im Server-Log.</p>
        )}

        {schritte.length > 0 && (
          <div className="bg-white/60 border border-red-200 rounded p-2">
            <GruppierteSchritte schritte={schritte} />
          </div>
        )}

        {/*
          Auch ein gescheiterter Lauf kann einen Bericht haben: bricht er NACH `POST /items`
          ab, steht ein teilangelegter Artikel inaktiv in Plenty (LAUF-06) — und genau dann
          ist die Frage „was ist davon da?" am wertvollsten. Ohne Bericht zeigt der Block
          nichts; der Fehlerfall bekommt also keine leere Karte dazu.
        */}
        {ergebnis?.nachkontrolle && (
          <NachkontrolleBlock
            nachkontrolle={ergebnis.nachkontrolle}
            onErneutPruefen={onErneutPruefen}
            laeuft={laeuft}
          />
        )}
      </div>
    );
  }

  // ---------- Nacharbeit offen (Status `teilweise`) ----------
  // STEHT ZWISCHEN den beiden alten Ausgaengen — nach dem harten Fehlerfall
  // (`jobStatus === 'failed'` bleibt rot) und vor dem Erfolgsfall. Der Lauf ist hier weder
  // das eine noch das andere: der Artikel ist angelegt, aber einzelne Schritte der
  // Nacharbeit sind gescheitert. Rot waere gefaehrlich — es verleitet zum Loeschen eines
  // Artikels, der in Ordnung ist; gruen waere gelogen. Also gelb, mit der Liste dessen,
  // was liegengeblieben ist.
  if (ergebnis && ergebnis.status === 'teilweise') {
    const fehlgeschlagen = schritte.filter((s) => s.status === 'fehler');
    // Aus den SCHRITTEN abgeleitet, nicht aus dem Status: `teilweise` kann kuenftig auch
    // andere Gruende haben, und dann waere der Barcode-Satz eine Behauptung. Er ist der
    // einzige Fall, der ueber „fehlt halt was" hinausgeht: die Plenty-Kopie bringt die
    // Barcodes des Originals MIT, und ein liegengebliebener bedeutet dieselbe EAN an zwei
    // Artikeln — harmlos, solange der neue inaktiv ist, ein Datenproblem beim Freischalten.
    const barcodeOffen = schritte.some(
      (s) => s.art === 'barcodeLoeschung' && s.status === 'fehler',
    );
    // Derselbe Weg fuer den mitkopierten Staffelpreis (Befund 23.09.2026) — und hier geht es
    // um den VERKAUFSPREIS: „ab Menge 4" gilt am Ballset je SET statt je Ball, vier Sets
    // kosteten damit den Staffelpreis eines einzelnen Balls.
    const preisOffen = schritte.some(
      (s) => s.art === 'preisLoeschung' && s.status === 'fehler',
    );
    return (
      <div className="rounded-lg border border-yellow-300 bg-yellow-50 text-[#1A1A1A] overflow-hidden">
        <div className="border-b border-yellow-300 px-4 py-3">
          <h2 className="text-sm font-bold flex items-center gap-2">
            <AlertTriangle size={16} /> Nacharbeit offen: {zahl(fehlgeschlagen.length)}{' '}
            {fehlgeschlagen.length === 1 ? 'Schritt' : 'Schritte'} fehlgeschlagen
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-normal ${badgeFarbe(ergebnis.status)}`}>
              {ergebnis.status}
            </span>
          </h2>
          <p className="text-xs mt-1">
            Artikel <strong>{ergebnis.ballsetItemId ?? '—'}</strong>
            {' · '}Nummer <strong>{ergebnis.ballsetNummer ?? '—'}</strong>
            {' · '}Hauptvariante <strong>{ergebnis.hauptVariationId ?? '—'}</strong>
            {ergebnis.laufId != null && <span> · Lauf #{ergebnis.laufId}</span>}
          </p>
        </div>

        <div className="p-4 space-y-3">
          <div className="rounded border border-yellow-300 bg-white/70 px-3 py-2">
            <p className="text-xs">
              Der Artikel <strong>ist angelegt</strong> und liegt inaktiv in Plenty — die
              folgenden Schritte sind liegengeblieben und müssen in Plenty von Hand
              nachgezogen werden. Ein zweiter Lauf legt ein <strong>zweites</strong> Ballset
              an, er wiederholt nicht die fehlenden Schritte.
            </p>
            {barcodeOffen && (
              <p className="mt-1.5 text-xs">
                <strong>Mitkopierte Barcodes stehen noch am neuen Artikel.</strong> Die
                Plenty-Kopie bringt die Barcodes des Originals mit; dieser Lauf hat sie nicht
                alle wieder entfernt. Sie müssen in Plenty entfernt werden,{' '}
                <strong>bevor</strong> der Artikel freigeschaltet wird — sonst trägt dieselbe
                EAN zwei Artikel.
              </p>
            )}
            {preisOffen && (
              <p className="mt-1.5 text-xs">
                <strong>Der mitkopierte Staffelpreis „ab Menge 4" steht noch am neuen
                Artikel.</strong> Am Ballset gilt er je <strong>Set</strong> statt je Ball —
                wer vier Sets kauft, zahlt sonst den Mengenpreis eines einzelnen Balls. In
                Plenty entfernen, <strong>bevor</strong> der Artikel freigeschaltet wird.
              </p>
            )}
            {fehlgeschlagen.length === 0 ? (
              <p className="mt-1.5 text-xs italic text-gray-600">
                Kein fehlgeschlagener Schritt im Ergebnis — Einzelheiten stehen im Server-Log.
              </p>
            ) : (
              <ul className="mt-1.5 space-y-1">
                {fehlgeschlagen.map((s) => (
                  <li key={s.id} className="text-[11px] break-words">
                    <span className="font-mono text-gray-600">{s.id}</span>{' '}
                    <span className="text-gray-700">({artText(s.art)})</span>
                    {s.fehler && <span className="text-red-800"> — {s.fehler}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <ErgebnisDetails
            ergebnis={ergebnis}
            schritte={schritte}
            setVarianten={setVarianten}
            onErneutPruefen={onErneutPruefen}
            laeuft={laeuft}
          />
        </div>
      </div>
    );
  }

  // ---------- Erfolgsfall ----------
  if (!ergebnis) {
    return (
      <div className={PANEL}>
        <p className="text-xs text-gray-500 italic">
          Der Lauf ist beendet, hat aber kein Ergebnis übermittelt.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-green-200 rounded-lg bg-white overflow-hidden">
      <div className="bg-green-50 border-b border-green-200 px-4 py-3">
        <h2 className="text-sm font-bold text-green-900 flex items-center gap-2">
          <CheckCircle2 size={16} /> Ballset angelegt
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-normal ${badgeFarbe(ergebnis.status)}`}>
            {ergebnis.status}
          </span>
        </h2>
        <p className="text-xs text-green-900 mt-1">
          Artikel <strong>{ergebnis.ballsetItemId ?? '—'}</strong>
          {' · '}Nummer <strong>{ergebnis.ballsetNummer ?? '—'}</strong>
          {' · '}Hauptvariante <strong>{ergebnis.hauptVariationId ?? '—'}</strong>
          {ergebnis.laufId != null && (
            <span className="text-green-800"> · Lauf #{ergebnis.laufId}</span>
          )}
        </p>
      </div>

      <div className="p-4 space-y-3">
        <ErgebnisDetails
          ergebnis={ergebnis}
          schritte={schritte}
          setVarianten={setVarianten}
          onErneutPruefen={onErneutPruefen}
          laeuft={laeuft}
        />

        <p className="text-xs text-[#1A1A1A] bg-[#E9FF57]/30 border border-[#DAD9D9] rounded px-3 py-2">
          Der Artikel liegt <strong>inaktiv</strong> in Plenty — inklusive Bilder,
          Eigenschaften, Tags, Versandprofile und Cross-Selling. Vor dem Freischalten in Plenty
          prüfen.
        </p>
      </div>
    </div>
  );
}
